import { z } from "zod";
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { NotFoundError } from "../error-classes";
import { dropUndefined, guardIsPlainObject } from "../utils";

// Email template status enum
export enum EmailTemplateStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  ARCHIVED = "archived",
}

// Template variables that can be used in HTML
export enum TemplateVariable {
  CUSTOMER_NAME = "{{customer_name}}",
  DATE = "{{date}}",
}

export interface EmailTemplate {
  name: string;
  description?: string;
  htmlContent: string;
  subject: string;
  status: EmailTemplateStatus;
  usedVariables: TemplateVariable[];
  createdBy: string; // User UID
  timestampCreated: Timestamp;
  timestampLastModified: Timestamp;
  version: number;
  deleted: boolean;
}

export interface ExistingEmailTemplate extends EmailTemplate {
  id: string;
  refPath: string;
}

export interface NewEmailTemplate {
  name: string;
  description?: string;
  htmlContent: string;
  subject: string;
  status?: EmailTemplateStatus;
  createdBy: string;
}

const EmailTemplateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(500).optional(),
  htmlContent: z.string().min(1).max(500000),
  subject: z.string().min(1).max(200),
  status: z.nativeEnum(EmailTemplateStatus).default(EmailTemplateStatus.DRAFT),
  usedVariables: z.array(z.nativeEnum(TemplateVariable)).default([]),
  createdBy: z.string().min(1).max(200),
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),
  version: z.number().int().min(1).default(1),
  deleted: z.boolean().default(false),
});

export const EmailTemplateManager = {
  /**
   * Convert the Document Snapshot into a validated ExistingEmailTemplate object.
   */
  createFromSnapshot: createEmailTemplateFromSnapshot,
  /** Drop `id` and `refPath` before saving to the database */
  convertForFirestore: convertEmailTemplateForFirestore,
  /** Use when validating something outgoing - writing to the DB or reading from the user */
  parse: validateEmailTemplate,
  /** Create a new template from a NewEmailTemplate request */
  createFromNewTemplate: createFromNewTemplate,
  /** Helper method to extract used variables from HTML content */
  extractVariablesFromHtml: extractVariablesFromHtml,
  /** Validate that the HTML contains valid template variables */
  validateTemplateVariables: validateTemplateVariables,
  /** Render template with actual data */
  renderTemplate: renderTemplate,
};

/**
 * Create a new template from a NewEmailTemplate request
 */
function createFromNewTemplate(newTemplate: NewEmailTemplate): EmailTemplate {
  const now = Timestamp.now();
  const usedVariables = extractVariablesFromHtml(newTemplate.htmlContent);

  return {
    name: newTemplate.name,
    description: newTemplate.description,
    htmlContent: newTemplate.htmlContent,
    subject: newTemplate.subject,
    status: newTemplate.status || EmailTemplateStatus.DRAFT,
    usedVariables,
    createdBy: newTemplate.createdBy,
    timestampCreated: now,
    timestampLastModified: now,
    version: 1,
    deleted: false,
  };
}

/**
 * Convert the Document Snapshot into a validated ExistingEmailTemplate object.
 */
function createEmailTemplateFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingEmailTemplate {
  if (!snapshot.exists()) {
    throw new NotFoundError(
      `EmailTemplate document does not exist: ${snapshot.ref.path}`,
    );
  }

  const data = snapshot.data();
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validateEmailTemplate(data),
  };
}

function convertEmailTemplateForFirestore(emailTemplate: EmailTemplate): any {
  const local = Object.assign({}, emailTemplate);
  return dropUndefined(local);
}

function validateEmailTemplate(value: unknown): EmailTemplate {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  return EmailTemplateSchema.parse(value);
}

// Helper method to extract used variables from HTML content
function extractVariablesFromHtml(htmlContent: string): TemplateVariable[] {
  const variables: TemplateVariable[] = [];
  const variableValues = Object.values(TemplateVariable);

  variableValues.forEach((variable) => {
    if (htmlContent.includes(variable)) {
      variables.push(variable);
    }
  });

  return variables;
}

// Validate that the HTML contains valid template variables
function validateTemplateVariables(htmlContent: string): string[] {
  const errors: string[] = [];
  const mustachePattern = /\{\{[^}]+\}\}/g;
  const matches = htmlContent.match(mustachePattern) || [];
  const validVariables = Object.values(TemplateVariable);

  matches.forEach((match) => {
    if (!validVariables.includes(match as TemplateVariable)) {
      errors.push(`Invalid template variable: ${match}`);
    }
  });

  return errors;
}

// Render template with actual data
function renderTemplate(
  htmlContent: string,
  variables: { [key in TemplateVariable]?: string },
): string {
  let rendered = htmlContent;
  rendered = rendered.replace(
    new RegExp(TemplateVariable.CUSTOMER_NAME, "g"),
    variables[TemplateVariable.CUSTOMER_NAME] || "",
  );
  rendered = rendered.replace(
    new RegExp(TemplateVariable.DATE, "g"),
    variables[TemplateVariable.DATE] || "",
  );
  return rendered;
}
